import {
  ClosingIcon,
  ClosingPill,
  NotaryLane,
  NotaryLaneStatus,
  NotarySegment,
  SlotVariant,
  isAlertVariant,
} from "./models/ron-monitor.models";

export const SLOT_MINUTES = 20;
export const SLOT_START_HOUR = 8;
export const SLOT_END_HOUR = 23;
export const VISIBLE_SLOTS = 9;
export const UNASSIGNED_LANE_ID = "unassigned";
export const UNASSIGNED_LANE_NAME = "Unassigned";
export const NO_VALUE = "—";

const MINUTES_PER_HOUR = 60;
const NOON_MINUTES = 12 * MINUTES_PER_HOUR;

export interface SlotColumn {
  index: number;
  startMinutes: number;
  label: string;
  meridiem: "am" | "pm";
}

export type SlotAxis = SlotColumn[];

export interface GridLane {
  laneId: string;
  name: string;
  segment: NotarySegment | null;
  status: NotaryLaneStatus | null;
  isUnassigned: boolean;
}

export interface SlotPresentation {
  variant: SlotVariant;
  label: string;
  background: string;
  foreground: string;
  icons: ClosingIcon[];
  iconNames: string[];
  renders: boolean;
}

interface VariantStyle {
  label: string;
  background: string;
  foreground: string;
  icons: ClosingIcon[];
  showsDelay: boolean;
  renders: boolean;
}

const VARIANT_STYLES: Readonly<Record<SlotVariant, VariantStyle>> = {
  completed: {
    label: "Completed",
    background: "var(--nova-gray-50)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  "in-session": {
    label: "In session",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["headphone", "person"],
    showsDelay: false,
    renders: true,
  },
  "pre-sign": {
    label: "In session",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["headphone", "person"],
    showsDelay: false,
    renders: true,
  },
  "check-in": {
    label: "Checking-in",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["person"],
    showsDelay: false,
    renders: true,
  },
  scheduled: {
    label: "Scheduled",
    background: "var(--nova-blue-100)",
    foreground: "var(--nova-blue-800)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  "open-slot": {
    label: "Open Slot",
    background: "var(--nova-gray-100)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  over: {
    label: "Over",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: ["headphone"],
    showsDelay: true,
    renders: true,
  },
  delayed: {
    label: "Delayed",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: [],
    showsDelay: true,
    renders: true,
  },
  stuck: {
    label: "Stuck",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: [],
    showsDelay: true,
    renders: true,
  },
  canceled: {
    label: "Canceled",
    background: "var(--nova-gray-200)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  empty: {
    label: "",
    background: "transparent",
    foreground: "transparent",
    icons: [],
    showsDelay: false,
    renders: false,
  },
};

const ICON_SPRITE_NAMES: Readonly<Record<ClosingIcon, string>> = {
  headphone: "headset_mic",
  person: "person",
  stamp: "notary",
};

export function iconSpriteName(icon: ClosingIcon): string {
  return ICON_SPRITE_NAMES[icon];
}

export function buildSlotAxis(): SlotAxis {
  const axis: SlotAxis = [];
  const firstMinutes = SLOT_START_HOUR * MINUTES_PER_HOUR;
  const lastMinutes = SLOT_END_HOUR * MINUTES_PER_HOUR;

  for (
    let startMinutes = firstMinutes;
    startMinutes <= lastMinutes;
    startMinutes += SLOT_MINUTES
  ) {
    axis.push({
      index: axis.length,
      startMinutes,
      label: formatSlotLabel(startMinutes),
      meridiem: startMinutes >= NOON_MINUTES ? "pm" : "am",
    });
  }

  return axis;
}

function formatSlotLabel(startMinutes: number): string {
  const hour24 = Math.floor(startMinutes / MINUTES_PER_HOUR);
  const minute = startMinutes % MINUTES_PER_HOUR;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${`${minute}`.padStart(2, "0")}`;
}

export function minutesFromMidnight(value: Date): number {
  return value.getHours() * MINUTES_PER_HOUR + value.getMinutes();
}

export function slotIndexForMinutes(minutes: number, axis: SlotAxis): number | null {
  if (axis.length === 0) {
    return null;
  }
  const offset = minutes - axis[0].startMinutes;
  if (offset < 0) {
    return null;
  }
  const index = Math.floor(offset / SLOT_MINUTES);
  return index < axis.length ? index : null;
}

export function currentSlotIndex(now: Date, axis: SlotAxis): number | null {
  return slotIndexForMinutes(minutesFromMidnight(now), axis);
}

export function bucketKey(laneId: string, slotIndex: number): string {
  return `${laneId}|${slotIndex}`;
}

export function laneIdForClosing(closing: ClosingPill): string {
  return closing.notaryId ?? UNASSIGNED_LANE_ID;
}

export function bucketClosings(
  closings: ClosingPill[],
  axis: SlotAxis
): Map<string, ClosingPill> {
  const buckets = new Map<string, ClosingPill>();

  closings.forEach((closing) => {
    const index = slotIndexForMinutes(
      minutesFromMidnight(new Date(closing.scheduledDateTime)),
      axis
    );
    if (index === null) {
      return;
    }

    const key = bucketKey(laneIdForClosing(closing), index);
    const existing = buckets.get(key);
    if (!existing || outranks(closing, existing)) {
      buckets.set(key, closing);
    }
  });

  return buckets;
}

function outranks(candidate: ClosingPill, existing: ClosingPill): boolean {
  const candidateAlert = isAlertVariant(candidate.variant);
  const existingAlert = isAlertVariant(existing.variant);
  if (candidateAlert !== existingAlert) {
    return candidateAlert;
  }
  return candidate.packageId < existing.packageId;
}

export function isOpenSlot(
  buckets: Map<string, ClosingPill>,
  laneId: string,
  slotIndex: number
): boolean {
  return !buckets.has(bucketKey(laneId, slotIndex));
}

export function buildGridLanes(
  lanes: NotaryLane[],
  closings: ClosingPill[]
): GridLane[] {
  const alertLaneIds = new Set(
    closings
      .filter((closing) => isAlertVariant(closing.variant))
      .map((closing) => laneIdForClosing(closing))
  );

  const assigned: GridLane[] = lanes.map((lane) => ({
    laneId: lane.notaryId,
    name: lane.name,
    segment: lane.segment,
    status: lane.currentStatus,
    isUnassigned: false,
  }));

  const sorted = [...assigned].sort((left, right) => {
    const leftAlert = alertLaneIds.has(left.laneId) ? 0 : 1;
    const rightAlert = alertLaneIds.has(right.laneId) ? 0 : 1;
    return leftAlert - rightAlert;
  });

  const covered = new Set(sorted.map((lane) => lane.laneId));
  const uncovered = Array.from(
    new Set(
      closings
        .map((closing) => laneIdForClosing(closing))
        .filter((laneId) => !covered.has(laneId))
    )
  ).sort();

  uncovered.forEach((laneId) =>
    sorted.push({
      laneId,
      name: laneId === UNASSIGNED_LANE_ID ? UNASSIGNED_LANE_NAME : laneId,
      segment: null,
      status: null,
      isUnassigned: true,
    })
  );

  return sorted;
}

export function clampWindowStart(
  start: number,
  totalSlots: number,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  const maxStart = Math.max(0, totalSlots - visibleSlots);
  if (start < 0) {
    return 0;
  }
  return start > maxStart ? maxStart : start;
}

export function centeredWindowStart(
  focusIndex: number | null,
  totalSlots: number,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  if (focusIndex === null) {
    return 0;
  }
  return clampWindowStart(
    focusIndex - Math.floor(visibleSlots / 2),
    totalSlots,
    visibleSlots
  );
}

export function initialWindowStart(
  now: Date | null,
  axis: SlotAxis,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  if (!now || axis.length === 0) {
    return 0;
  }

  const focusIndex = currentSlotIndex(now, axis);
  if (focusIndex !== null) {
    return centeredWindowStart(focusIndex, axis.length, visibleSlots);
  }

  const minutes = minutesFromMidnight(now);
  if (minutes > axis[axis.length - 1].startMinutes) {
    return clampWindowStart(axis.length, axis.length, visibleSlots);
  }
  return 0;
}

export function slotPresentation(
  variant: SlotVariant,
  delayMinutes: number | null,
  icon: ClosingIcon | null
): SlotPresentation {
  const style = VARIANT_STYLES[variant];
  const label =
    style.showsDelay && delayMinutes !== null
      ? `${style.label} · ${delayMinutes} min`
      : style.label;
  const icons = icon ? [icon] : style.icons;

  return {
    variant,
    label,
    background: style.background,
    foreground: style.foreground,
    icons,
    iconNames: icons.map(iconSpriteName),
    renders: style.renders,
  };
}

export function openSlotPresentation(): SlotPresentation {
  return slotPresentation("open-slot", null, null);
}

export function matchingLaneIds(
  term: string,
  lanes: GridLane[],
  closings: ClosingPill[]
): Set<string> {
  const needle = term.trim().toLowerCase();
  if (needle === "") {
    return new Set<string>();
  }

  const matches = new Set<string>();

  lanes.forEach((lane) => {
    if (lane.name.toLowerCase().includes(needle)) {
      matches.add(lane.laneId);
    }
  });

  closings.forEach((closing) => {
    if (closing.variant === "canceled") {
      return;
    }
    if (
      closing.loanNumber.toLowerCase().includes(needle) ||
      closing.orderNumber.toLowerCase().includes(needle)
    ) {
      matches.add(laneIdForClosing(closing));
    }
  });

  return matches;
}

export function formatClockTime(value: Date): string {
  const minutes = minutesFromMidnight(value);
  const meridiem = minutes >= NOON_MINUTES ? "pm" : "am";
  return `${formatSlotLabel(minutes)} ${meridiem}`;
}

const MONTH_NAMES: readonly string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function isSameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function formatTimeRelativeTo(value: Date, reference: Date): string {
  const time = formatClockTime(value);
  if (isSameDay(value, reference)) {
    return time;
  }
  return `${MONTH_NAMES[value.getMonth()]} ${value.getDate()}, ${time}`;
}

function startTime(closing: ClosingPill): number {
  return new Date(closing.scheduledDateTime).getTime();
}

export function nextSlotForClosing(
  current: ClosingPill,
  closings: ClosingPill[]
): ClosingPill | null {
  const laneId = laneIdForClosing(current);
  const currentStart = startTime(current);

  return closings.reduce<ClosingPill | null>((best, candidate) => {
    if (candidate.packageId === current.packageId) {
      return best;
    }
    if (laneIdForClosing(candidate) !== laneId) {
      return best;
    }
    if (candidate.variant === "canceled" || candidate.variant === "empty") {
      return best;
    }

    const candidateStart = startTime(candidate);
    if (!(candidateStart > currentStart)) {
      return best;
    }
    if (best === null) {
      return candidate;
    }

    const bestStart = startTime(best);
    if (candidateStart < bestStart) {
      return candidate;
    }
    if (candidateStart === bestStart && candidate.packageId < best.packageId) {
      return candidate;
    }
    return best;
  }, null);
}

export function nextSlotLabel(
  current: ClosingPill,
  closings: ClosingPill[]
): string {
  const next = nextSlotForClosing(current, closings);
  return next ? formatClockTime(new Date(next.scheduledDateTime)) : NO_VALUE;
}

export type ClosingStatusClass =
  | "active"
  | "scheduled"
  | "canceled"
  | "completed"
  | "none";

export interface PanelAction {
  label: string;
  icon: string | null;
  disabled: boolean;
}

const ACTIVE_VARIANTS: readonly SlotVariant[] = [
  "over",
  "delayed",
  "stuck",
  "in-session",
  "pre-sign",
  "check-in",
];

export function closingStatusClass(variant: SlotVariant): ClosingStatusClass {
  if (ACTIVE_VARIANTS.includes(variant)) {
    return "active";
  }
  if (variant === "scheduled") {
    return "scheduled";
  }
  if (variant === "canceled") {
    return "canceled";
  }
  if (variant === "completed") {
    return "completed";
  }
  return "none";
}

const PANEL_ACTIONS: Readonly<
  Record<ClosingStatusClass, readonly PanelAction[]>
> = {
  active: [
    { label: "Call notary", icon: "phone", disabled: false },
    { label: "Text notary", icon: "chat", disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  scheduled: [
    { label: "Contact participants", icon: null, disabled: false },
    { label: "View closing details", icon: null, disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  canceled: [
    { label: "Reschedule appointment", icon: null, disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  completed: [
    { label: "View closing details", icon: null, disabled: true },
    { label: "View completion summary", icon: null, disabled: true },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  none: [],
};

export function panelActions(variant: SlotVariant): PanelAction[] {
  return PANEL_ACTIONS[closingStatusClass(variant)].map((action) => ({
    ...action,
  }));
}
